/**
 * Global Styles for MiniApp
 * Telegram native design system
 * EXACT MATCH to sample1.jpg, sample2.jpg, sample3.jpg
 */

import { createGlobalStyle } from 'styled-components';

const GlobalStyles = createGlobalStyle`
  :root {
    /* Telegram Theme Variables - These are overridden by App.tsx with actual Telegram theme */
    --tg-theme-bg-color: var(--tg-color-scheme-bg, #ffffff);
    --tg-theme-text-color: var(--tg-color-scheme-text, #000000);
    --tg-theme-hint-color: var(--tg-color-scheme-hint, #999999);
    --tg-theme-link-color: var(--tg-color-scheme-link, #2481cc);
    --tg-theme-button-color: var(--tg-color-scheme-button, #3390ec);
    --tg-theme-button-text-color: var(--tg-color-scheme-button-text, #ffffff);
    --tg-theme-secondary-bg-color: var(--tg-color-scheme-secondary-bg, #f0f0f0);

    /* Design System - Use Telegram theme variables */
    --bg-primary: var(--tg-theme-bg-color, #ffffff);
    --bg-secondary: var(--tg-theme-secondary-bg-color, #f0f0f0);
    --bg-card: var(--tg-theme-secondary-bg-color, #f0f0f0);
    --text-primary: var(--tg-theme-text-color, #000000);
    --text-secondary: var(--tg-theme-hint-color, #999999);
    --accent-green: var(--tg-theme-button-color, #3390ec);
    --accent-blue: var(--tg-theme-link-color, #2481cc);
    --accent-red: #FF3B30;
    --border-color: var(--tg-theme-hint-color, rgba(0, 0, 0, 0.1));

    /* Spacing - Exact from samples */
    --spacing-xs: 4px;
    --spacing-sm: 8px;
    --spacing-md: 12px;
    --spacing-lg: 16px;
    --spacing-xl: 20px;

    /* Border Radius - Match samples */
    --radius-sm: 8px;
    --radius-md: 12px;
    --radius-lg: 16px;
    --radius-xl: 20px;
    --radius-full: 50%;

    /* Font Sizes - Match samples */
    --font-xs: 11px;
    --font-sm: 13px;
    --font-md: 15px;
    --font-lg: 17px;
    --font-xl: 22px;
    --font-xxl: 28px;
    --font-huge: 48px;

    /* Font Weights */
    --font-regular: 400;
    --font-medium: 500;
    --font-semibold: 600;
    --font-bold: 700;

    /* Shadows */
    --shadow-sm: 0 1px 3px rgba(0, 0, 0, 0.3);
    --shadow-md: 0 2px 8px rgba(0, 0, 0, 0.4);

    /* Transitions */
    --transition: 200ms ease;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  html, body {
    width: 100%;
    height: 100%;
    overflow-x: hidden;
  }

  body {
    font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
    background-color: var(--tg-theme-bg-color);
    color: var(--tg-theme-text-color);
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    touch-action: manipulation;
    overflow-x: hidden;
  }

  #root {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
  }

  /* Typography */
  h1, h2, h3, h4, h5, h6 {
    font-weight: var(--font-bold);
    line-height: 1.2;
    color: var(--text-primary);
    margin: 0;
  }

  h1 {
    font-size: var(--font-xxl);
  }

  h2 {
    font-size: var(--font-xl);
  }

  h3 {
    font-size: var(--font-lg);
  }

  p {
    font-size: var(--font-md);
    line-height: 1.5;
    color: var(--text-primary);
    margin: 0;
  }

  small {
    font-size: var(--font-sm);
    color: var(--text-secondary);
  }

  /* Links */
  a {
    color: var(--accent-green);
    text-decoration: none;
    transition: opacity var(--transition);

    &:active {
      opacity: 0.7;
    }
  }

  /* Buttons */
  button {
    font-family: inherit;
    font-size: var(--font-md);
    font-weight: var(--font-medium);
    cursor: pointer;
    border: none;
    outline: none;
    background: none;
    color: inherit;
    transition: all var(--transition);
    -webkit-tap-highlight-color: transparent;

    &:active {
      transform: scale(0.98);
      opacity: 0.8;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  /* Inputs */
  input, textarea, select {
    font-family: inherit;
    font-size: var(--font-md);
    outline: none;
    border: none;
    background: none;
    color: inherit;
    -webkit-tap-highlight-color: transparent;
  }

  input::placeholder,
  textarea::placeholder {
    color: var(--text-secondary);
  }

  /* Scrollbar */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--text-secondary);
    border-radius: var(--radius-sm);

    &:hover {
      background: var(--text-primary);
    }
  }

  /* Selection */
  ::selection {
    background: var(--accent-green);
    color: var(--text-primary);
  }

  /* Animations */
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes slideDown {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }

  /* Telegram native haptic feedback */
  .haptic-feedback {
    -webkit-tap-highlight-color: transparent;
  }

  /* Card styles */
  .card {
    background: var(--bg-card);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
    transition: all var(--transition);
  }

  /* Container */
  .container {
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
    padding: var(--spacing-lg);
  }

  /* Remove tap highlight on mobile */
  * {
    -webkit-tap-highlight-color: transparent;
    -webkit-touch-callout: none;
  }

  /* iOS safe area */
  @supports (padding: max(0px)) {
    body {
      padding-top: max(0px, env(safe-area-inset-top));
      padding-bottom: max(0px, env(safe-area-inset-bottom));
    }
  }
`;

export default GlobalStyles;
